import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import baseUri from '../../../api/baseUri.ts';
import {ProjectDataIndex} from '../../../api/types.ts';
import {useAuthStateManager} from '../../../hooks/useAuthStateManager.tsx';
import useSceneViewerToken from '../../../api/scene/useSceneViewerToken.ts';
import useFetchProjectSceneScriptAndHtml from '../../../api/scene/useFetchProjectSceneScriptAndHtml.ts';
import {TabView, TabViewController, TabViewState, useTabView} from '../../../lib/tabView.ts';
import {
    SceneStore,
    openSceneStore,
    sceneStoreKey
} from './sceneStorageStore.ts';

// Must match the CSP sandbox sent with scene.html (SceneEndpoints.SceneFrameSandbox). No allow-same-origin and
// no top navigation: the author's code gets an opaque origin and can't reach the app or the viewer's session.
export const SCENE_FRAME_SANDBOX =
    'allow-scripts allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox ' +
    'allow-downloads allow-pointer-lock allow-orientation-lock';

export const SCENE_FRAME_ALLOW =
    'fullscreen *; autoplay *; clipboard-write *; gamepad *';

export interface SceneFrameHandle {
    readonly contentWindow: Window | null;
    // Loads the frame afresh, with the latest stored data.
    reload(): void;
    focus(): void;
}

type IframeProps = Omit<
    React.IframeHTMLAttributes<HTMLIFrameElement>,
    'src' | 'name' | 'sandbox' | 'allow' | 'allowFullScreen'
>;

interface SceneFrameProps extends IframeProps {
    projectId: string;
    dataIndex: ProjectDataIndex;
    syncToken: string;
}

/*
 * Every frame that runs a project's own code (scene, vibe-code and blocks
 * tabs) goes through here. The frame is sandboxed, so it has no storage of its
 * own and no session; this hands it what it needs through window.name, which
 * the injected shim (scene-auth.js) reads before any author script runs:
 *   - its stored localStorage data (sceneStorageStore), and
 *   - a read-only scene token, so a private project's data loads for viewers
 *     who may see it.
 * The shim posts storage changes back; they're accepted only from frames this
 * component mounted.
 *
 * Chrome and Safari don't pass a changed name into a frame that has already
 * loaded, so every load is a fresh mount (keyed), including reload(), and so
 * is a frame that reloaded itself and arrived without its data ('reloaded').
 */
const SceneFrame = forwardRef<SceneFrameHandle, SceneFrameProps>(
    ({projectId, dataIndex, syncToken, title, ...iframeProps}, ref) => {
        const {isAuthenticated, loggedInUserId} = useAuthStateManager();
        const {token, ready: tokenReady} = useSceneViewerToken(projectId);

        const fetchScene = useFetchProjectSceneScriptAndHtml(
            projectId,
            dataIndex
        );
        const fetchSceneRef = useRef(fetchScene);
        fetchSceneRef.current = fetchScene;

        const storeKey = sceneStoreKey(
            isAuthenticated ? loggedInUserId : null,
            projectId,
            dataIndex
        );
        const [opened, setOpened] = useState<{
            key: string;
            store: SceneStore;
        } | null>(null);
        const store = opened?.key === storeKey ? opened.store : null;

        useEffect(() => {
            let cancelled = false;
            openSceneStore(storeKey, async () => {
                const scene = await fetchSceneRef.current();
                return `${scene?.sceneHtml ?? ''}\n${scene?.sceneScript ?? ''}`;
            }).then(opened => {
                if (!cancelled) {
                    setOpened({key: storeKey, store: opened});
                }
            });
            return () => {
                cancelled = true;
            };
        }, [storeKey]);

        useEffect(() => {
            return () => {
                store?.flush();
            };
        }, [store]);

        const [nonce, setNonce] = useState(0);
        const src = `${baseUri}/api/scene/${projectId}/${dataIndex}/${syncToken}/scene.html`;
        const ready = tokenReady && store !== null;
        const frameKey = `${src}#${nonce}#${token ?? ''}#${storeKey}`;

        // Built once per mount, from the data as it stands then.
        const name = useMemo(
            () =>
                ready
                    ? JSON.stringify({
                          mx: 'scene-v1',
                          p: projectId,
                          d: dataIndex,
                          t: token,
                          s: store.snapshot()
                      })
                    : '',
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [frameKey, ready]
        );

        // Each window this component has mounted, with the store its writes belong to. A frame being replaced
        // still sends its last changes as it unloads, so those are kept too.
        const frames = useRef(new WeakMap<object, SceneStore>());
        const iframeRef = useRef<HTMLIFrameElement | null>(null);
        const storeRef = useRef(store);
        storeRef.current = store;
        const remounts = useRef<number[]>([]);

        const attach = useCallback((iframe: HTMLIFrameElement | null) => {
            iframeRef.current = iframe;
            if (iframe?.contentWindow && storeRef.current) {
                frames.current.set(iframe.contentWindow, storeRef.current);
            }
        }, []);

        /*
         * The view (lib/tabView.ts): the frame's own shim (scene-auth.js) says
         * when the page has finished drawing and, in a Babylon scene, moves the
         * camera. Requests are re-sent until answered, since a frame that is
         * still loading has no listener yet.
         */
        const renderReplies = useRef(
            new Map<number, (reply: {state?: TabViewState; error?: string}) => void>()
        );
        const nextRenderId = useRef(1);
        const viewController = useMemo<TabViewController>(() => {
            const ask = (op: 'ready' | 'view' | 'freeze' | 'thaw', view?: TabView | null) =>
                new Promise<TabViewState>((resolve, reject) => {
                    const id = nextRenderId.current++;
                    const started = Date.now();
                    let resend: number | undefined;
                    const send = () => {
                        iframeRef.current?.contentWindow?.postMessage(
                            {source: 'mixiply-host', action: 'render', id, op, view: view ?? null},
                            '*'
                        );
                        if (Date.now() - started > 45_000) {
                            renderReplies.current.delete(id);
                            reject(new Error('The frame did not answer.'));
                            return;
                        }
                        resend = window.setTimeout(send, 1000);
                    };
                    renderReplies.current.set(id, reply => {
                        window.clearTimeout(resend);
                        renderReplies.current.delete(id);
                        if (reply.error) {
                            reject(new Error(reply.error));
                        } else {
                            resolve(reply.state ?? {});
                        }
                    });
                    send();
                });

            return {
                ready: () => ask('ready').then(() => undefined),
                apply: view => {
                    // A blocks player starts on its green flag, which the hosting page may press.
                    if (view?.start) {
                        iframeRef.current?.contentWindow?.postMessage({type: 'mixiply-blocks-flag'}, '*');
                    }
                    return ask('view', view);
                },
                freeze: () => ask('freeze').then(() => undefined),
                thaw: () => ask('thaw').then(() => undefined)
            };
        }, []);
        useTabView(dataIndex, viewController);

        useEffect(() => {
            const onMessage = (event: MessageEvent) => {
                const data = event.data as {
                    source?: string;
                    action?: string;
                    ops?: unknown;
                    id?: number;
                    state?: TabViewState;
                    error?: string;
                } | null;
                // A sandboxed frame's messages come from the opaque origin, "null".
                if (
                    event.origin !== 'null' ||
                    !event.source ||
                    data?.source !== 'mixiply-frame'
                ) {
                    return;
                }
                if (
                    data.action === 'render' &&
                    event.source === iframeRef.current?.contentWindow &&
                    typeof data.id === 'number'
                ) {
                    renderReplies.current.get(data.id)?.({state: data.state, error: data.error});
                    return;
                }
                const frameStore = frames.current.get(event.source);
                if (!frameStore) {
                    return;
                }
                if (data.action === 'storage') {
                    frameStore.apply(data.ops);
                } else if (
                    data.action === 'reloaded' &&
                    event.source === iframeRef.current?.contentWindow
                ) {
                    // The page reloaded itself and lost its data on the way (see scene-auth.js): load it again
                    // with the data. A few times a minute at most, so a page that can't take it doesn't loop.
                    const now = Date.now();
                    remounts.current = remounts.current.filter(
                        at => now - at < 60_000
                    );
                    if (remounts.current.length < 3) {
                        remounts.current.push(now);
                        setNonce(n => n + 1);
                    }
                }
            };
            window.addEventListener('message', onMessage);
            return () => window.removeEventListener('message', onMessage);
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                get contentWindow() {
                    return iframeRef.current?.contentWindow ?? null;
                },
                reload: () => setNonce(n => n + 1),
                focus: () => iframeRef.current?.focus()
            }),
            []
        );

        if (!ready) {
            return (
                <div
                    className={iframeProps.className}
                    style={iframeProps.style}
                    aria-busy="true"
                />
            );
        }

        return (
            <iframe
                key={frameKey}
                ref={attach}
                {...iframeProps}
                title={title ?? 'Project content'}
                src={src}
                name={name}
                sandbox={SCENE_FRAME_SANDBOX}
                allow={SCENE_FRAME_ALLOW}
                allowFullScreen
            />
        );
    }
);

SceneFrame.displayName = 'SceneFrame';

export default SceneFrame;
